import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { FormInput } from '~/components/FormComponents/FormInput';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import useGetContragentTypes from '~/modules/identification/hooks/useGetContragentTypes';
import { useFormContext, useWatch } from 'react-hook-form';
import { TPurposeOfBusinessRelations } from '../types';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip';
import { useKvedsQuery } from '~/queries/useKvedQuery.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { commonStyles } from '~/theme/styles/commonStyles';

interface INACEInfoProps {
  tooltipTitle: string;
}

export const NACEInfo = ({ tooltipTitle }: INACEInfoProps) => {
  const theme = useTheme();
  const { control, setValue } = useFormContext<TPurposeOfBusinessRelations>();
  const { activityKinds, edrRequest } = useKvedsQuery();
  const { isLegalResident } = useGetContragentTypes();
  const [typesOfActivity, businessDescription] = useWatch({
    name: ['typesOfActivity', 'businessDescription'],
    control,
  });

  useEffect(() => {
    if (!typesOfActivity?.value && edrRequest) {
      setValue('typesOfActivity', activityKinds[0] ?? null, { shouldDirty: false });
    }
  }, [edrRequest, typesOfActivity, activityKinds]);

  return (
    <Grid container spacing={{ xs: 1, sm: 1, md: 2 }} sx={{ width: '100%' }}>
      <Grid sx={{ position: 'relative' }} size={{ xs: 12, md: 12 }}>
        <FormInput
          name="businessDescription"
          label={
            <Box sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
              <Typography variant="body2">Зміст та суть діяльності (не КВЕД)</Typography>
              <InfoTooltip title={tooltipTitle} />
            </Box>
          }
          placeholder="Введіть зміст та суть діяльності (не КВЕД)"
          multiline
          rows={4}
          fullWidth
        />
        <Typography
          sx={{ color: 'textSecondary', position: 'absolute', bottom: 16, right: 16 }}
          variant="body2"
        >
          <Box
            component={'span'}
            sx={{
              color: isLegalResident
                ? businessDescription?.length > 4000
                  ? theme.palette.error.main
                  : 'inherit'
                : 'inherit',
            }}
          >
            {businessDescription?.length}
          </Box>
          {isLegalResident ? ' / 4000' : null}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <FormAutocomplete
          options={activityKinds}
          name="typesOfActivity"
          disabled={edrRequest}
          inputProps={{
            placeholder: 'Оберіть основний КВЕД',
          }}
          fullWidth
          label="Основний КВЕД"
        />
      </Grid>
      {isLegalResident && (
        <>
          <FormGridItem>
            <FormInput
              name="registeredShareCapital"
              label="Розмір статутного капіталу зареєстрований (тис. гривень)"
              placeholder="Введіть розмір статутного капіталу зареєстрований (тис. гривень)"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="paidShareCapital"
              label="Розмір статутного капіталу фактично сплачений (тис. гривень)"
              placeholder="Введіть розмір статутного капіталу фактично сплачений (тис. гривень)"
              fullWidth
            />
          </FormGridItem>
        </>
      )}
    </Grid>
  );
};
